import { WebSocketTransport } from '@epicgames-ps/lib-pixelstreamingcommon-ue5.5'
import {
  Config,
  PixelStreaming,
  WebRtcPlayerController,
} from '@epicgames-ps/lib-pixelstreamingfrontend-ue5.5'
import type { Span } from '@opentelemetry/api'
import { SpanStatusCode, trace } from '@opentelemetry/api'
import {
  ATTR_KESTREL_DEV_MODE,
  ATTR_KESTREL_ICE_RETRY_COUNT,
  ATTR_KESTREL_MODEL_ID,
  ATTR_KESTREL_VM_ID,
} from 'features/honeycomb'
import type {
  ConnectionConfig,
  ConnectionManagerEventMap,
  ConnectionState,
  ErrorEventDetail,
  MessageEventDetail,
  ResetRequestEventDetail,
  StateChangeEventDetail,
} from 'features/kestrelViewer/connection/types'
import type {
  ChunkBuffer,
  ParsedMessage,
} from 'features/kestrelViewer/utils/messageUtils'
import {
  emitPixelStreamUIInteraction,
  parseMessage,
  processChunkedMessage,
} from 'features/kestrelViewer/utils/messageUtils'

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_STREAMER_TIMEOUT_MS = 10000
const LOG_PREFIX = '[PixelStream]'

/**
 * Time to wait for ICE to connect before considering it stuck.
 * If ICE is still in "checking" state after this, we'll retry.
 */
const ICE_CONNECTION_TIMEOUT_MS = 5000

/**
 * Maximum number of automatic retry attempts when ICE gets stuck.
 */
const MAX_ICE_RETRY_ATTEMPTS = 5

// ============================================================================
// Internal Types
// ============================================================================

/**
 * Extended WebRTC controller type for monkey-patching.
 */
interface WebRtcControllerWithPrev extends WebRtcPlayerController {
  prevOnMessage?: (event: MessageEvent) => void
}

// ============================================================================
// PixelStreamConnectionManager
// ============================================================================

/**
 * Manages the lifecycle of a PixelStreaming connection.
 *
 * This class encapsulates all connection logic in a sequential, cancellable flow:
 * 1. Create PixelStreaming instance
 * 2. Patch WebSocket handlers (if not dev mode)
 * 3. Connect and wait for WebSocket open
 * 4. Send authentication token
 * 5. Wait for streamer list (with timeout)
 * 6. Ready for video streaming
 *
 * Events emitted:
 * - `stateChange`: Connection state transitions
 * - `message`: Raw WebRTC messages from Unreal
 * - `error`: Connection errors
 * - `resetRequest`: When a new session should be requested (timeout, subscribe failed)
 */
const TRACER = trace.getTracer('forecast')

export class PixelStreamConnectionManager extends EventTarget {
  private state: ConnectionState = { status: 'idle' }
  private abortController?: AbortController
  private messageChunks = new Map<string, ChunkBuffer>()

  // Resolvers for promise-based waiting
  private webSocketOpenResolver?: () => void
  private streamerResolver?: () => void
  private streamerTimeoutId?: ReturnType<typeof setTimeout>

  // Track ICE retry attempts
  private iceRetryCount = 0
  private iceTimeoutId?: ReturnType<typeof setTimeout>

  // Active kestrel.connect span for the current connection attempt
  private connectSpan?: Span

  // ============================================================================
  // Public API
  // ============================================================================

  /**
   * Get the current connection state.
   */
  getState(): ConnectionState {
    return this.state
  }

  /**
   * Get the PixelStreaming instance if one exists.
   */
  getInstance(): PixelStreaming | undefined {
    if ('instance' in this.state) {
      return this.state.instance
    }
    return undefined
  }

  /**
   * Connect to the pixel streaming server.
   *
   * This is the main entry point. It runs through the connection sequence:
   * 1. Create instance
   * 2. Patch WebSocket handlers
   * 3. Connect
   * 4. Authenticate
   * 5. Wait for streamer
   *
   * The process can be cancelled at any point via `disconnect()`.
   *
   * @param config - Connection configuration
   * @param isRetry - Internal flag indicating this is an automatic retry (don't reset retry count)
   */
  async connect(config: ConnectionConfig, isRetry = false): Promise<void> {
    // Reset retry count for fresh connection attempts (not retries)
    if (!isRetry) {
      this.iceRetryCount = 0
    }

    // Clear any existing ICE timeout
    if (this.iceTimeoutId) {
      clearTimeout(this.iceTimeoutId)
      this.iceTimeoutId = undefined
    }

    // Abort any existing connection attempt
    this.abortController?.abort()
    this.abortController = new AbortController()
    const { signal } = this.abortController

    // End any leftover connect span from a previous attempt
    this.endConnectSpan({ code: SpanStatusCode.OK })

    // Start kestrel.connect span for this attempt
    this.connectSpan = TRACER.startSpan('kestrel.connect', {
      attributes: {
        [ATTR_KESTREL_DEV_MODE]: config.devMode,
        [ATTR_KESTREL_ICE_RETRY_COUNT]: this.iceRetryCount,
        ...(config.kestrelModelId && {
          [ATTR_KESTREL_MODEL_ID]: config.kestrelModelId,
        }),
        ...(config.vmId && { [ATTR_KESTREL_VM_ID]: config.vmId }),
      },
    })

    try {
      // Step 1: Create instance
      this.connectSpan?.addEvent('kestrel.connect.creating_instance')
      this.transitionTo({ status: 'creating-instance' })
      const instance = this.createPixelStreamingInstance(config)
      this.throwIfAborted(signal)

      // Step 2: Transition to connecting with instance
      this.connectSpan?.addEvent('kestrel.connect.connecting')
      this.transitionTo({ status: 'connecting', instance })

      // Step 3: Set up WebSocket handlers and message processing
      this.setupMessageHandlers(instance)

      if (!config.devMode) {
        // Patch WebSocket for auth flow
        this.patchWebSocketHandlers(instance, config, signal)
      }

      // Step 4: Connect
      instance.connect()

      if (config.devMode) {
        // In dev mode, skip auth flow and go straight to ready
        // Dev mode connects directly to a Windows VM without our auth layer
        this.connectSpan?.addEvent('kestrel.connect.ready')
        this.transitionTo({ status: 'ready', instance })
        this.endConnectSpan({ code: SpanStatusCode.OK })
        return
      }

      // Step 5: Wait for WebSocket open (auth will be sent in patched handler)
      this.connectSpan?.addEvent('kestrel.connect.authenticating')
      this.transitionTo({ status: 'authenticating', instance })
      await this.waitForWebSocketOpen(signal)

      // Step 6: Wait for streamer with timeout
      this.connectSpan?.addEvent('kestrel.connect.waiting_for_streamer')
      this.transitionTo({ status: 'waiting-for-streamer', instance })
      await this.waitForStreamer(
        signal,
        config.streamerTimeoutMs ?? DEFAULT_STREAMER_TIMEOUT_MS
      )

      // Step 7: Ready!
      this.connectSpan?.addEvent('kestrel.connect.ready')
      this.transitionTo({ status: 'ready', instance })

      // Set up ICE timeout monitoring - if ICE doesn't connect, auto-retry
      this.setupIceTimeoutMonitoring(instance, config)
      this.endConnectSpan({ code: SpanStatusCode.OK })
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') {
        // Aborted - this is expected during disconnect, not an error
        this.connectSpan?.addEvent('kestrel.connect.aborted')
        this.endConnectSpan({ code: SpanStatusCode.OK })
        return
      }
      if (e instanceof Error) {
        this.endConnectSpan(
          { code: SpanStatusCode.ERROR, message: String(e) },
          e
        )
      } else {
        this.endConnectSpan({ code: SpanStatusCode.ERROR })
        console.error('Unexpected error', e)
      }
      this.handleError(e)
    }
  }

  /**
   * Disconnect from the pixel streaming server.
   * Aborts any in-progress connection attempt and cleans up resources.
   */
  disconnect(): void {
    // End active kestrel.connect span if still open (e.g. abort handler didn't run yet)
    this.connectSpan?.addEvent('kestrel.connect.aborted')
    this.endConnectSpan({ code: SpanStatusCode.OK })

    // Abort any in-progress async operations immediately (synchronous!)
    this.abortController?.abort()
    this.abortController = undefined

    // Clear any pending timeouts
    if (this.streamerTimeoutId) {
      clearTimeout(this.streamerTimeoutId)
      this.streamerTimeoutId = undefined
    }

    // Clear ICE timeout monitoring
    if (this.iceTimeoutId) {
      clearTimeout(this.iceTimeoutId)
      this.iceTimeoutId = undefined
    }

    // Clear resolvers
    this.webSocketOpenResolver = undefined
    this.streamerResolver = undefined

    // Disconnect the instance if we have one
    const instance = this.getInstance()
    if (instance) {
      try {
        instance.removeResponseEventListener('kestrelResponse')
        instance.disconnect()
      } catch (e) {
        console.warn('Error disconnecting PixelStreaming instance:', e)
      }
    }

    // Clear message chunk buffer
    this.messageChunks.clear()

    // Reset to idle state
    this.transitionTo({ status: 'idle' })
  }

  /**
   * Send a command to the Unreal Engine via the pixel streaming connection.
   *
   * @param json - The command to send (string or object)
   * @returns true if the command was sent, false if no connection exists
   */
  sendCommand(json: string | object): boolean {
    const instance = this.getInstance()
    if (!instance) {
      return false
    }
    return emitPixelStreamUIInteraction(instance, json)
  }

  // ============================================================================
  // Event Emission (typed helpers)
  // ============================================================================

  private emitStateChange(
    state: ConnectionState,
    previousState: ConnectionState
  ): void {
    const detail: StateChangeEventDetail = { state, previousState }
    this.dispatchEvent(new CustomEvent('stateChange', { detail }))
  }

  private emitMessage(message: ParsedMessage): void {
    const detail: MessageEventDetail = { message }
    this.dispatchEvent(new CustomEvent('message', { detail }))
  }

  private emitError(error: string, recoverable: boolean): void {
    const detail: ErrorEventDetail = { error, recoverable }
    this.dispatchEvent(new CustomEvent('error', { detail }))
  }

  private emitResetRequest(reason: ResetRequestEventDetail['reason']): void {
    const detail: ResetRequestEventDetail = { reason }
    this.dispatchEvent(new CustomEvent('resetRequest', { detail }))
  }

  // ============================================================================
  // State Management
  // ============================================================================

  private transitionTo(newState: ConnectionState): void {
    const previousState = this.state
    this.state = newState
    this.emitStateChange(newState, previousState)
  }

  // ============================================================================
  // Instance Creation
  // ============================================================================

  private createPixelStreamingInstance(
    config: ConnectionConfig
  ): PixelStreaming {
    // Clear any existing children in the video parent
    while (config.videoParent.firstChild) {
      config.videoParent.removeChild(config.videoParent.firstChild)
    }

    const defaultSettings = {
      AutoPlayVideo: true,
      AutoConnect: false, // We connect manually after patching handlers
      ss: config.url,
      StartVideoMuted: true,
      HoveringMouse: true,
      WaitForStreamer: true,
      WebRTCMinBitrate: 3000, // 3 Mbps minimum
      WebRTCMaxBitrate: 100000, // 100 Mbps maximum
    }

    const pixelConfig = new Config({ initialSettings: defaultSettings })
    const instance = new PixelStreaming(pixelConfig, {
      videoElementParent: config.videoParent,
    })

    // Store previous message handler for monkey-patching
    const controller = instance.webRtcController as WebRtcControllerWithPrev
    controller.prevOnMessage = controller.handleOnMessage.bind(controller)

    return instance
  }

  // ============================================================================
  // WebSocket Patching
  // ============================================================================

  private patchWebSocketHandlers(
    instance: PixelStreaming,
    config: ConnectionConfig,
    signal: AbortSignal
  ): void {
    const transport = instance.signallingProtocol
      .transport as WebSocketTransport

    // Patch handleOnOpen to send auth when WebSocket opens
    const prevOnOpen = transport.handleOnOpen.bind(transport)
    transport.handleOnOpen = () => {
      prevOnOpen()

      // Check if aborted before proceeding
      if (signal.aborted) return

      // Extract just the token from "Bearer <token>"
      const token = config.authToken.startsWith('Bearer ')
        ? config.authToken.slice(7)
        : config.authToken

      transport.sendMessage(
        JSON.stringify({
          type: 'geckoToken',
          value: token,
        })
      )

      // Resolve the WebSocket open promise
      this.webSocketOpenResolver?.()
    }

    // Patch handleOnMessage to detect streamerList and handle failures
    const prevOnMessage = transport.handleOnMessage.bind(transport)
    transport.handleOnMessage = (e: MessageEvent) => {
      if (signal.aborted) return

      if (typeof e.data === 'string') {
        // Handle plain text authentication failures gracefully
        if (e.data === 'Authentication failed') {
          this.emitError('Authentication failed', false)
          this.emitResetRequest('auth-failed')
          return // Don't pass to prevOnMessage as it will try to JSON.parse and crash
        }

        // Check for streamerList and subscribeFailed messages
        try {
          const parsed = JSON.parse(e.data)

          if (parsed.type === 'streamerList') {
            if (parsed.ids && parsed.ids.length > 0) {
              // Streamer available! Resolve the wait promise
              this.streamerResolver?.()
            }
            // Empty list - keep waiting (timeout will handle if no streamer arrives)
          } else if (parsed.type === 'subscribeFailed') {
            const failureMessage =
              typeof parsed.message === 'string' ? parsed.message : ''

            if (failureMessage.toLowerCase().includes('does not exist')) {
              console.warn('Model does not exist, requesting new VM...', parsed)
              this.emitResetRequest('subscribe-failed')
              return
            }
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', e.data, error)
        }
      }

      // Pass to original handler
      try {
        prevOnMessage(e)
      } catch (error) {
        console.error('Error in WebSocket message handler:', error)
      }
    }
  }

  // ============================================================================
  // Message Handling
  // ============================================================================

  private setupMessageHandlers(instance: PixelStreaming): void {
    // Handler for processing incoming messages (from both sources)
    const processIncomingMessage = (dataMessage: string) => {
      const dataJson = parseMessage(dataMessage)
      if (!dataJson) return

      // Handle chunked messages
      if (dataJson.__chunkType === 'partial' && dataJson.__messageId) {
        const completeMessage = processChunkedMessage(
          dataJson.__messageId,
          this.messageChunks,
          {
            __totalChunks: Number(dataJson.__totalChunks),
            __chunkIndex: Number(dataJson.__chunkIndex),
            __chunkData: dataJson.__chunkData as string,
          }
        )

        if (completeMessage) {
          try {
            const completeJson = JSON.parse(completeMessage) as ParsedMessage
            this.emitMessage(completeJson)
          } catch (error) {
            console.error('Error parsing reassembled message:', error)
          }
        }
      } else {
        this.emitMessage(dataJson)
      }
    }

    // Listen for response events from PixelStreaming
    instance.addResponseEventListener('kestrelResponse', processIncomingMessage)

    // Monkey-patch WebRTC data channel message handler
    const webRtcController =
      instance.webRtcController as WebRtcControllerWithPrev
    webRtcController.handleOnMessage = (ev: MessageEvent) => {
      const dec = new TextDecoder('utf-8')
      const dataMessage = dec.decode(ev.data).replaceAll('\x00', '')
      processIncomingMessage(dataMessage)

      // Call original handler
      webRtcController.prevOnMessage?.(ev)
    }
  }

  // ============================================================================
  // ICE Timeout Monitoring and Retry
  // ============================================================================

  /**
   * Set up monitoring for ICE connection timeout.
   * If ICE stays stuck in "checking" state, automatically retry the connection.
   */
  private setupIceTimeoutMonitoring(
    instance: PixelStreaming,
    config: ConnectionConfig
  ): void {
    if (this.iceTimeoutId) {
      clearTimeout(this.iceTimeoutId)
    }

    // @ts-expect-error - accessing internals
    const pc = instance._webRtcController?.peerConnectionController
      ?.peerConnection as RTCPeerConnection | undefined

    if (!pc) {
      return
    }

    if (
      pc.iceConnectionState === 'connected' ||
      pc.iceConnectionState === 'completed'
    ) {
      this.iceRetryCount = 0
      return
    }

    // Listen for ICE connection state changes
    const onIceConnectionStateChange = () => {
      if (
        pc.iceConnectionState === 'connected' ||
        pc.iceConnectionState === 'completed'
      ) {
        if (this.iceTimeoutId) {
          clearTimeout(this.iceTimeoutId)
          this.iceTimeoutId = undefined
        }
        this.iceRetryCount = 0 // Reset retry count on success
        pc.removeEventListener(
          'iceconnectionstatechange',
          onIceConnectionStateChange
        )
      } else if (pc.iceConnectionState === 'failed') {
        if (this.iceTimeoutId) {
          clearTimeout(this.iceTimeoutId)
          this.iceTimeoutId = undefined
        }
        pc.removeEventListener(
          'iceconnectionstatechange',
          onIceConnectionStateChange
        )
        this.handleIceStuck(config)
      }
    }
    pc.addEventListener('iceconnectionstatechange', onIceConnectionStateChange)

    // Set timeout for ICE connection
    this.iceTimeoutId = setTimeout(() => {
      this.iceTimeoutId = undefined
      pc.removeEventListener(
        'iceconnectionstatechange',
        onIceConnectionStateChange
      )

      if (
        pc.iceConnectionState !== 'connected' &&
        pc.iceConnectionState !== 'completed'
      ) {
        console.warn(
          `${LOG_PREFIX} ICE stuck in "${pc.iceConnectionState}" after ${ICE_CONNECTION_TIMEOUT_MS}ms`
        )
        this.handleIceStuck(config)
      }
    }, ICE_CONNECTION_TIMEOUT_MS)
  }

  /**
   * Handle ICE connection getting stuck - attempt automatic retry.
   */
  private async handleIceStuck(config: ConnectionConfig): Promise<void> {
    if (this.iceRetryCount >= MAX_ICE_RETRY_ATTEMPTS) {
      console.error(
        `${LOG_PREFIX} ICE connection failed after ${MAX_ICE_RETRY_ATTEMPTS} retry attempts`
      )
      this.emitError(
        `ICE connection failed after ${MAX_ICE_RETRY_ATTEMPTS} retries`,
        true
      )
      return
    }

    this.iceRetryCount++

    // Disconnect current connection
    this.disconnect()

    // Retry connection (pass isRetry=true to preserve retry count)
    await this.connect(config, true)
  }

  // ============================================================================
  // Promise-based Waiting
  // ============================================================================

  private waitForWebSocketOpen(signal: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      this.webSocketOpenResolver = resolve

      // Handle abort
      signal.addEventListener(
        'abort',
        () => {
          this.webSocketOpenResolver = undefined
          reject(new DOMException('Aborted', 'AbortError'))
        },
        { once: true }
      )
    })
  }

  private waitForStreamer(
    signal: AbortSignal,
    timeoutMs: number
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal.aborted) {
        reject(new DOMException('Aborted', 'AbortError'))
        return
      }

      // Set up timeout
      this.streamerTimeoutId = setTimeout(() => {
        this.streamerResolver = undefined
        console.warn(
          `No streamer available after ${timeoutMs}ms, requesting new VM...`
        )
        this.emitResetRequest('timeout')
        // Don't reject - let the reset request handler deal with it
        // The component will call disconnect() which will abort this promise
      }, timeoutMs)

      this.streamerResolver = () => {
        if (this.streamerTimeoutId) {
          clearTimeout(this.streamerTimeoutId)
          this.streamerTimeoutId = undefined
        }
        resolve()
      }

      // Handle abort
      signal.addEventListener(
        'abort',
        () => {
          if (this.streamerTimeoutId) {
            clearTimeout(this.streamerTimeoutId)
            this.streamerTimeoutId = undefined
          }
          this.streamerResolver = undefined
          reject(new DOMException('Aborted', 'AbortError'))
        },
        { once: true }
      )
    })
  }

  // ============================================================================
  // Error Handling
  // ============================================================================

  private handleError(e: unknown): void {
    const errorMessage = e instanceof Error ? e.message : String(e)
    console.error('PixelStreamConnectionManager error:', e)

    const instance = this.getInstance()
    this.transitionTo({ status: 'error', error: errorMessage, instance })
    this.emitError(errorMessage, false)
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  private throwIfAborted(signal: AbortSignal): void {
    if (signal.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }
  }

  /**
   * End the active kestrel.connect span and clear it. Safe to call multiple times.
   */
  private endConnectSpan(
    status: { code: SpanStatusCode; message?: string },
    recordException?: Error
  ): void {
    const span = this.connectSpan
    this.connectSpan = undefined
    if (!span) return
    if (recordException !== undefined) {
      span.recordException(recordException)
    }
    span.setStatus(status)
    span.end()
  }
}

// ============================================================================
// Type-safe event listener helpers
// ============================================================================

type ConnectionManagerEventListener<K extends keyof ConnectionManagerEventMap> =
  (event: ConnectionManagerEventMap[K]) => void

/**
 * Add a typed event listener to the connection manager.
 */
export function addConnectionManagerListener<
  K extends keyof ConnectionManagerEventMap,
>(
  manager: PixelStreamConnectionManager,
  type: K,
  listener: ConnectionManagerEventListener<K>
): void {
  manager.addEventListener(type, listener as EventListener)
}

/**
 * Remove a typed event listener from the connection manager.
 */
export function removeConnectionManagerListener<
  K extends keyof ConnectionManagerEventMap,
>(
  manager: PixelStreamConnectionManager,
  type: K,
  listener: ConnectionManagerEventListener<K>
): void {
  manager.removeEventListener(type, listener as EventListener)
}
